import { useCallback, useEffect, useRef, useState } from "react";
import { useAppSelector } from "@/app/hooks";
import {
  referralService,
  type ReferralItem,
  type ReferralLinkData,
  type ReferralStatistics,
  type ReferralStatusFilter,
  type GetReferralHistoryPayload,
  type MyReferralsPayload,
} from "@/features/referrals/services/referral.service";

type ReferralStats = {
  total: number;
  pending: number;
  completed: number;
};

export const useReferralSettings = () => {
  const user = useAppSelector((state) => state.auth.user);
  const username = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim();

  const [referralCount, setReferralCount] = useState(0);
  const [transactions, setTransactions] = useState<ReferralItem[]>([]);
  const [statistics, setStatistics] = useState<ReferralStatistics | null>(null);
  const [referralsList, setReferralsList] = useState<ReferralItem[]>([]);
  const [referralStats, setReferralStats] = useState<ReferralStats>({
    total: 0,
    pending: 0,
    completed: 0,
  });
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(false);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ReferralStatusFilter>("all");
  const [copied, setCopied] = useState(false);
  const [referralLinkData, setReferralLinkData] = useState<ReferralLinkData | null>(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralLink, setReferralLink] = useState<string | null>(null);
  const [isAdminApprove, setIsAdminApprove] = useState(false);
  const [isUserRequested, setIsUserRequested] = useState(false);

  const copyTimeoutRef = useRef<number | null>(null);

  const referralShareUrl = referralLink || "";

  const fetchReferralData = useCallback(async () => {
    if (!user?.id) return;

    try {
      const response = await referralService.getReferralData();
      if (response?.status === "success" && response.data) {
        setReferralCode(response.data.referral_code ?? "");
        setReferralLink(response.data.referral_link ?? null);
        setReferralLinkData(response.data.referral_link_data ?? null);
        setReferralCount(response.data.total_referrals ?? 0);
        setStatistics({
          totalPointsUsed: response.data.total_credits ?? 0,
        });
        setReferralStats((prev) => ({
          ...prev,
          total: response.data.total_referrals ?? 0,
          completed: response.data.confirmed_referrals ?? 0,
        }));
        setIsAdminApprove(Boolean(response.data.is_admin_approve));
        setIsUserRequested(Boolean(response.data.is_user_requested));
      }
    } catch (error) {
      console.error("Error fetching referral data:", error);
    }
  }, [user?.id]);

  const handleGenerateLink = useCallback(async () => {
    if (!user?.id || isGeneratingLink) return;
    if (referralLinkData) return;

    try {
      setIsGeneratingLink(true);
      await referralService.generateReferralCode();
      await fetchReferralData();
    } catch (error) {
      console.error("Error generating referral link", error);
    } finally {
      setIsGeneratingLink(false);
    }
  }, [user?.id, isGeneratingLink, referralLinkData, fetchReferralData]);

  const fetchReferralHistory = useCallback(
    async (payload: GetReferralHistoryPayload) => {
      if (!user?.id) return;
      try {
        setIsLoadingTransactions(true);
        const response = await referralService.getReferralHistory(payload);
        if (response.status === "success" && response.data) {
          const referrals = response.data.referrals ?? [];
          setTransactions(referrals);
          setReferralStats((prev) => ({
            ...prev,
            pending: referrals.filter((item) => item.status?.toLowerCase() === "pending").length,
          }));
        }
      } catch (error) {
        console.error("Error fetching referral history:", error);
      } finally {
        setIsLoadingTransactions(false);
      }
    },
    [user?.id]
  );

  const fetchMyReferrals = useCallback(
    async (payload?: MyReferralsPayload) => {
      if (!user?.id) return;

      try {
        setIsLoadingReferrals(true);
        const resolvedPayload: MyReferralsPayload = payload ?? {
          page: 1,
          limit: 10,
          role: "",
          status: statusFilter,
          search: "",
        };

        const normalizedPayload = {
          ...resolvedPayload,
          status: resolvedPayload.status === "all" ? "" : resolvedPayload.status,
        };

        const response = await referralService.myReferrals(normalizedPayload);
        if (response.status === "success" && response.data) {
          const referrals = response.data.referrals ?? [];
          setReferralsList(referrals);
          setReferralStats({
            total: response.data.total ?? 0,
            pending: referrals.filter((item) => item.status?.toLowerCase() === "pending").length,
            completed: referrals.filter((item) => item.status?.toLowerCase() === "completed")
              .length,
          });
        }
      } catch (error) {
        console.error("Error fetching my referrals:", error);
      } finally {
        setIsLoadingReferrals(false);
      }
    },
    [statusFilter, user?.id]
  );

  const copyReferralCode = useCallback(async () => {
    const toCopy = referralLink || referralCode;
    if (!toCopy) return;

    try {
      await navigator.clipboard.writeText(toCopy);
      setCopied(true);
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => setCopied(false), 1200);
    } catch (error) {
      console.error("Error copying referral link:", error);
    }
  }, [referralLink, referralCode]);

  const shareReferralCode = useCallback(async () => {
    if (!referralShareUrl) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Join The Andy App",
          text: "Join The Andy App and save on property taxes!",
          url: referralShareUrl,
        });
      } else {
        await copyReferralCode();
      }
      await fetchReferralHistory({ page: 1, limit: 10 });
    } catch (error) {
      console.error("Error sharing referral link:", error);
    }
  }, [referralShareUrl, copyReferralCode, fetchReferralHistory]);

  const openEmail = useCallback(() => {
    if (!referralShareUrl) return;
    const email = `mailto:?subject=Join The Andy App&body=${encodeURIComponent(
      `Join The Andy App and save on property taxes!\n\nUse my referral link: ${referralShareUrl}`
    )}`;
    window.open(email, "_blank");
  }, [referralShareUrl]);

  const openSMS = useCallback(() => {
    if (!referralShareUrl) return;
    const body = encodeURIComponent(
      `Join The Andy App and save on property taxes!\n\nUse my referral link: ${referralShareUrl}`
    );
    window.open(`sms:?body=${body}`, "_self");
  }, [referralShareUrl]);

  useEffect(() => {
    fetchReferralData();
    fetchReferralHistory({ page: 1, limit: 10 });
    fetchMyReferrals();
  }, [statusFilter, fetchReferralHistory, fetchMyReferrals, fetchReferralData]);

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    },
    []
  );

  return {
    user,
    username,
    referralCode,
    referralShareUrl,
    referralCount,
    transactions,
    statistics,
    referralsList,
    referralStats,
    statusFilter,
    copied,
    referralLinkData,
    isGeneratingLink,
    isAdminApprove,
    isUserRequested,
    referralLink,
    isLoadingReferrals,
    isLoadingTransactions,
    setStatusFilter,
    copyReferralCode,
    shareReferralCode,
    openEmail,
    openSMS,
    handleGenerateLink,
    fetchReferralHistory,
    fetchMyReferrals,
  };
};
